import { Component, computed, effect, ElementRef, inject, OnDestroy, OnInit, ViewChild, Renderer2 } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { StyleClassModule } from 'primeng/styleclass';
import { LayoutService } from '@/layout/service/layout.service';
import { AppBreadcrumb } from './app.breadcrumb';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { RippleModule } from 'primeng/ripple';
import { BadgeModule } from 'primeng/badge';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { AvatarModule } from 'primeng/avatar';
import { FormsModule } from '@angular/forms';
import { LoginService } from '@/services/login.service';
import { UserService } from '@/services/user.service';
import { ClientService } from '@/services/client.service';
import { TooltipModule } from 'primeng/tooltip';
import { ProfileComponent } from '@/pages/profile/profile.component';
import pagesRoutes from '@/pages/pages.routes';
import { NotificationService } from '@/services/notification.service';
import { AnnouncementsService } from '@/services/announcements.service';
import { UserManualService } from '@/services/user-manual.service';
import { PrivilegeService } from '@/services/privilege.service';
import { finalize, Subscription, timer } from 'rxjs';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { Dialog } from "primeng/dialog";

@Component({
    selector: '[app-topbar]',
    standalone: true,
    imports: [
        RouterModule,
        CommonModule,
        FormsModule,
        StyleClassModule,
        InputTextModule,
        ButtonModule,
        IconFieldModule,
        InputIconModule,
        RippleModule,
        BadgeModule,
        OverlayBadgeModule,
        AvatarModule,
        TooltipModule,
        AppBreadcrumb,
        ProfileComponent,
        BadgeModule,
        ToggleSwitch,
        Dialog
    ],
    template: ` <div class="layout-topbar">
        <!-- Desktop puts the brand lock-up and the menu bar in app-sidebar, so
             this block only surfaces on mobile: hamburger, back, breadcrumb. -->
        <div class="topbar-left">
            <a tabindex="0" #menubutton type="button" class="menu-button" (click)="onMenuButtonClick()">
                <i class="pi pi-bars"></i>
            </a>
            <span class="topbar-separator"></span>
            <!-- No back button of our own here: app-breadcrumb renders one
                 itself (same canGoBack rule), so a second one next to it just
                 drew the arrow twice on mobile. -->
            <div class="topbar-breadcrumb" app-breadcrumb></div>
            <!-- <a routerLink="/">
                <img class="mobile-logo" src="/layout/images/logo-{{ isDarkTheme() ? 'white' : 'dark' }}.svg"
                         alt="logo"/>
            </a> -->
        </div>

        <div class="topbar-right">
            <ul class="topbar-menu">

                <li *ngIf="privilegeService.hasPrivilege('SHOW_USER_MANUAL')" class="sm:relative right-sidebar-item">
                    <p-button
                        tooltipPosition="bottom"
                        pTooltip="User Manual"
                        [appendTo]="'body'"
                        [tooltipZIndex]="'20000'"
                        [rounded]="true"
                        styleClass="topbar-btn gradient-bg custom-announcement-button"
                        pStyleClass="@next"
                        enterFromClass="hidden"
                        enterActiveClass="animate-scalein"
                        leaveActiveClass="animate-fadeout"
                        leaveToClass="hidden"
                        [hideOnOutsideClick]="true"
                        (click)="loadUserManuals()"
                    >
                        <a class="right-sidebar-button">
                            <i class="pi pi-book"></i>
                        </a>
                    </p-button>

                    <div #userManualDropdown class="topbar-overlay list-none m-0 rounded-2xl border border-surface overflow-hidden absolute bg-surface-0 dark:bg-surface-900 hidden origin-top w-[360px] mt-2 right-0 z-[10000] shadow-xl">
                        <!-- Header -->
                        <div class="flex justify-between items-center px-3 py-3 border-b manual-header">
                            <span class="font-semibold">User manuals</span>
                            <span class="manual-count">{{ userManuals.length }} available</span>
                        </div>

                        <div *ngIf="userManualLoading" class="notification-loader-container">
                            <div class="notification-loader"></div>
                        </div>

                        <!-- Empty -->
                        <div *ngIf="!userManualLoading && userManuals.length === 0" class="flex flex-col items-center py-8 text-surface-500">
                            <i class="pi pi-book text-3xl mb-2"></i>
                            <span>No user manuals</span>
                        </div>

                        <!-- List -->
                        <ul *ngIf="!userManualLoading && userManuals.length" class="flex flex-col max-h-80 overflow-auto">
                            <li *ngFor="let manual of userManuals" class="manual-item">
                                <span class="manual-type" [ngClass]="manualExtension(manual)">
                                    <i [ngClass]="manualExtension(manual) === 'pdf' ? 'pi pi-file-pdf' : 'pi pi-file-word'"></i>
                                </span>

                                <div class="manual-details">
                                    <p class="manual-name" tooltipPosition="bottom">
                                        {{ manualFileName(manual) }}
                                    </p>
                                    <small *ngIf="manualMeta(manual)" class="manual-meta">{{ manualMeta(manual) }}</small>
                                </div>

                                <button type="button" pButton pTooltip="Download" tooltipPosition="left" [rounded]="false" class="manual-download-btn p-button-outlined p-button-sm" (click)="downloadUserManual(manual)">
                                    <i class="pi pi-download"></i>
                                </button>
                            </li>
                        </ul>

                        
                    </div>
                </li>

                <li *ngIf="privilegeService.hasPrivilege('SHOW_ANNOUNCEMENT')" (click)="openAnnouncement()" class="sm:relative right-sidebar-item">
                    <p-button tooltipPosition="bottom" pTooltip="Announcements" [appendTo]="'body'" [tooltipZIndex]="'20000'" [rounded]="true" styleClass="topbar-btn gradient-bg custom-announcement-button">
                        <a class="right-sidebar-button">
                            <i class="pi pi-megaphone"></i>
                        </a>

                        <span *ngIf="announcementCount > 0" class="absolute -top-2 left-5 bg-red-500 text-white text-xs px-1.5 rounded-full font-semibold z-100">
                            {{ announcementCount > 9 ? '9+' : announcementCount }}
                        </span>
                    </p-button>
                </li>

                <li *ngIf="hasSelectedClient() && loginService.isSuperUser()" (click)="exitClient()" class="right-sidebar-item">
                    <p-button tooltipPosition="bottom" pTooltip="Exit Client" [appendTo]="'body'" [tooltipZIndex]="'20000'" [rounded]="true" size="large" styleClass="topbar-btn">
                        <a class="right-sidebar-button">
                            <i class="pi pi-sign-out"></i>
                        </a>
                    </p-button>
                </li>

                <!-- <li (click)="toggleRenderedPage()" class="right-sidebar-item">
                    <p-button tooltipPosition="bottom" [pTooltip]="'Go to ' + currentRenderedPage" [appendTo]="'body'" [tooltipZIndex]="'20000'" [rounded]="true" styleClass="topbar-btn">
                        <a class="right-sidebar-button">
                            <i [ngClass]="currentRenderedPage == 'Documents' ? 'pi pi-file' : 'pi pi-microchip'"></i>
                        </a>
                    </p-button>
                </li> -->

                <li (click)="toggleTheme()" class="right-sidebar-item">
                    <p-button tooltipPosition="bottom" [appendTo]="'body'" [tooltipZIndex]="'20000'" [pTooltip]="isDarkTheme() ? 'Light Mode' : 'Dark Mode'" [rounded]="true" size="large" styleClass="topbar-btn">
                        <a class="right-sidebar-button">
                            <i [ngClass]="isDarkTheme() ? 'pi pi-sun' : 'pi pi-moon'"></i>
                        </a>
                    </p-button>
                </li>

                <!-- <li (click)="layoutService.toggleConfigSidebar()" class="right-sidebar-item">
                <p-button tooltipPosition="bottom" pTooltip="Settings" [appendTo]="'body'" [tooltipZIndex]="'20000'" [rounded]="true" styleClass="topbar-btn">
                    <a class="right-sidebar-button">
                        <i class="pi pi-cog"></i>
                    </a>
                </p-button>
            </li> -->
                <li (click)="refresh()" class="right-sidebar-item">
                    <p-button tooltipPosition="bottom" pTooltip="Refresh" [appendTo]="'body'" [tooltipZIndex]="'20000'" [rounded]="true" size="large" styleClass="topbar-btn">
                        <a class="right-sidebar-button">
                            <i class="pi pi-refresh"></i>
                        </a>
                    </p-button>
                </li>
                <li class="sm:relative right-sidebar-item">
                    <p-button
                        tooltipPosition="bottom"
                        pTooltip="Notifications"
                        [appendTo]="'body'"
                        [tooltipZIndex]="'20000'"
                        [rounded]="true"
                        size="large"
                        styleClass="topbar-btn custom-bell-button"
                        pStyleClass="@next"
                        enterFromClass="hidden"
                        enterActiveClass="animate-scalein"
                        leaveActiveClass="animate-fadeout"
                        leaveToClass="hidden"
                        [hideOnOutsideClick]="true"
                        (click)="loadNotifications()"
                    >
                        <a class="right-sidebar-button">
                            <i class="pi pi-bell"></i>
                        </a>

                        <span *ngIf="count > 0" class="absolute -top-2 left-5 bg-red-500 text-white text-xs px-1.5 rounded-full font-semibold z-100">
                            {{ count > 9 ? '9+' : count }}
                        </span>
                    </p-button>

                    <div #notificationDropdown class="topbar-overlay list-none m-0 rounded-2xl border border-surface overflow-hidden absolute bg-surface-0 dark:bg-surface-900 hidden origin-top w-[260px] mt-2 right-0 z-[10000] shadow-xl">
                        <!-- Header -->
                        <div class="flex justify-between items-center px-2 py-3 border-b notification-header"><span class="font-semibold">Notifications</span><p-badge [value]="count" severity="secondary" /></div>

                        <div *ngIf="notificationLoading" class="notification-loader-container">
                            <div class="notification-loader"></div>
                        </div>

                        <!-- Empty -->
                        <div *ngIf="!notificationLoading && notifications.length === 0" class="flex flex-col items-center py-8 text-surface-500">
                            <i class="pi pi-inbox text-3xl mb-2"></i>
                            <span>No notifications</span>
                        </div>

                        <!-- List -->
                        <ul *ngIf="!notificationLoading && notifications.length" class="flex flex-col max-h-80 overflow-auto">
                            <li *ngFor="let n of notifications" class="relative p-2 transition border-b hover:bg-surface-200 group">
                                <div class="flex-1 cursor-pointer" (click)="markRead(n)">
                                    <div class="flex items-center gap-2">
                                        <p
                                            [ngClass]="{
                                                'font-semibold': !n.is_read,
                                                'text-surface-500': n.is_read
                                            }"
                                        >
                                            {{ n.document_transaction_id }} | {{ n.title }}
                                        </p>
                                    </div>

                                    <p
                                        [ngClass]="{
                                            'font-normal': !n.is_read,
                                            'text-surface-500': n.is_read
                                        }"
                                    >
                                        {{ n.short_description }}
                                    </p>
                                    <small class="text-xs text-surface-400">
                                        {{ n.created_on | date: 'yyyy-MM-dd HH:mm:ss' : 'UTC' }}
                                    </small>
                                </div>

                                <button
                                    type="button"
                                    pButton
                                    pTooltip="Clear"
                                    tooltipPosition="top"
                                    [rounded]="true"
                                    class="p-button-sm clr-btn right-0 p-button-text !p-0 !w-7 !h-7 min-w-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                    (click)="delete([n.id])"
                                >
                                    <i class="pi pi-trash text-red-500"></i>
                                </button>
                            </li>
                        </ul>

                        <!-- footer -->
                        <div *ngIf="!notificationLoading && notifications.length" class="flex justify-between items-center px-2 py-2 border-t">
                            <span (click)="markAllRead()" class="cursor-pointer hover:underline text-sm">Mark all read</span>
                            <span (click)="deleteAll()" class="cursor-pointer hover:underline text-sm">Clear all</span>
                        </div>
                    </div>
                </li>
                <li class="profile-item static sm:relative">
                    <a
                        class="right-sidebar-button relative z-50 flex align-item-center"
                        pStyleClass="@next"
                        enterFromClass="hidden"
                        enterActiveClass="animate-scalein"
                        leaveActiveClass="animate-fadeout"
                        leaveToClass="hidden"
                        [hideOnOutsideClick]="true"
                    >
                        <p-avatar [label]="briefName" styleClass="!w-10 !h-10 mr-2" [style]="{ 'background-color': userRole === 'System Super User' ? 'var(--avatar-bg-superuser)' : 'var(--avatar-bg-user)' }" shape="circle" />

                        <div class="flex flex-col mr-2">
                            <!-- The top bar is Dark Gray in every mode, so the
                                 name is always white. -->
                            <span class="text-medium line-height text-white">{{ userName }}</span>
                            <span class="text-xs p-1 rounded-md role-badge" [ngClass]="userRole === 'System Super User' ? 'role-badge-super' : 'role-badge-user'">{{ userRole }}</span>
                        </div>
                        <span class="pi pi-chevron-down"></span>
                    </a>
                    <div
                        class="topbar-overlay list-none p-2 m-0 rounded-2xl border border-surface overflow-hidden absolute bg-surface-0 dark:bg-surface-900 hidden origin-top w-52 mt-2 right-0 z-[10000] top-auto shadow-[0px_56px_16px_0px_rgba(0,0,0,0.00),0px_36px_14px_0px_rgba(0,0,0,0.01),0px_20px_12px_0px_rgba(0,0,0,0.02),0px_9px_9px_0px_rgba(0,0,0,0.03),0px_2px_5px_0px_rgba(0,0,0,0.04)]"
                    >
                        <ul class="flex flex-col gap-1">
                            <li>
                                <a (click)="openProfile()" class="label-small dark:text-surface-400 flex gap-2 py-2 px-2.5 rounded-lg items-center hover:bg-emphasis transition-colors duration-150 cursor-pointer">
                                    <i class="pi pi-user"></i>
                                    <span>Profile</span>
                                </a>
                            </li>
                            <app-profile *ngIf="showProfile" (closed)="onProfileClosed()"> </app-profile>

                            <!-- <li>
                                <a (click)="layoutService.toggleConfigSidebar()" class="label-small dark:text-surface-400 flex gap-2 py-2 px-2.5 rounded-lg items-center hover:bg-emphasis transition-colors duration-150 cursor-pointer">
                                    <i class="pi pi-cog"></i>
                                    <span>Settings</span>
                                </a>
                            </li> -->
                            <li (click)="logout()">
                                <a class="label-small dark:text-surface-400 flex gap-2 py-2 px-2.5 rounded-lg items-center hover:bg-emphasis transition-colors duration-150 cursor-pointer">
                                    <i class="pi pi-power-off"></i>
                                    <span>Log out</span>
                                </a>
                            </li>
                        </ul>
                    </div>
                </li>
                <!-- <li class="right-sidebar-item">
                        <a tabindex="0" class="right-sidebar-button" (click)="showRightMenu()">
                            <i class="pi pi-align-right"></i>
                        </a>
                    </li> -->
            </ul>
             <!-- appendTo body is not optional here: .layout-topbar is
                  position: fixed and 3.5rem tall, so an inline dialog's mask
                  sizes itself to that strip and the dialog centres inside the
                  top bar instead of the viewport. baseZIndex clears the chrome,
                  which sits at 10000. -->
             <p-dialog
            [(visible)]="announcementDialog"
            [modal]="true"
            [draggable]="false"
            [resizable]="false"
            [closable]="false"
            [showHeader]="false"
            [appendTo]="'body'"
            [baseZIndex]="20000"
            [style]="{ width: '800px', }"
            styleClass="announcement-viewer-dialog"
            scrollable="true">

            <!-- ============================= -->
            <!-- HEADER -->
            <!-- ============================= -->

            <div class="announcement-header">

                <h2>
                    Announcements
                </h2>

                <button
                    type="button"
                    class="announcement-close-btn"
                    (click)="closeAnnouncement()">

                    <i class="pi pi-times"></i>

                </button>

            </div>


            <!-- ============================= -->
            <!-- CONTENT -->
            <!-- ============================= -->

            <div class="announcement-content">

                <!-- ============================= -->
                <!-- ANNOUNCEMENTS (ACCORDION) -->
                <!-- ============================= -->

                <div class="announcement-list">

                    @if (announcementLoading) {

                        <div class="announcement-loading">

                            <i class="pi pi-spin pi-spinner"></i>

                            <span>
                                Loading announcements...
                            </span>

                        </div>

                    } @else {

                    @for (
                        announcement of announcements;
                        track announcement.id;
                        let i = $index
                    ) {

                        <div
                            class="announcement-card"
                            [class.expanded]="isAnnouncementExpanded(announcement.id)">


                            <!-- TITLE + DATE (ACCORDION HEADER) -->

                            <div class="announcement-card-header">

                                <button
                                    type="button"
                                    class="announcement-toggle"
                                    [attr.aria-expanded]="isAnnouncementExpanded(announcement.id)"
                                    (click)="toggleAnnouncement(announcement.id)">

                                    <i
                                        class="pi announcement-toggle-icon"
                                        [ngClass]="isAnnouncementExpanded(announcement.id) ? 'pi-chevron-down' : 'pi-chevron-right'">
                                    </i>

                                    <h3>
                                        {{ announcement.title }}
                                    </h3>

                                    <!-- <span class="announcement-date">

                                        {{
                                            announcement.active_on
                                                | date:'yyyy-MM-dd'
                                        }}

                                    </span> -->

                                </button>


                                <!-- ATTACHMENT DOWNLOAD -->

                                @if (announcement.file_content || !announcement.file_content) {

                                    <button
                                        type="button"
                                        class="announcement-download-btn" 
                                       (click)="downloadAnnouncementFile(announcement)"
                                        [disabled]="!announcement.file_content?.trim()">

                                        <i class="pi pi-download"></i>

                                    </button>

                                }

                            </div>


                            <!-- ACCORDION BODY -->

                            @if (isAnnouncementExpanded(announcement.id)) {

                                <div class="announcement-card-body">

                                    <!-- DESCRIPTION -->

                                    <div
                                        class="announcement-description"
                                        [innerHTML]="announcement.long_desc || announcement.short_desc">
                                    </div>

                                </div>

                            }

                        </div>

                    } @empty {

                        <div class="no-announcements">

                            <i class="pi pi-bell"></i>

                            <span>
                                No announcements available
                            </span>

                        </div>

                    }

                    }

                </div>

            </div>

    </p-dialog>
        </div>
     
      
    </div>
    

    `,
    styles: `
        :host ::ng-deep .p-button.custom-bell-button,
        :host ::ng-deep .p-button.custom-announcement-button {
            overflow: visible !important;
        }
        .notification-header {
            background: var(--btn-bg);
            color: var(--btn-color);
        }

        /* ============================= */
        /* USER MANUALS DROPDOWN         */
        /* ============================= */

        /* Plain surface rather than the notification header's brand band: this
           list is reference material, not something demanding attention. */
        .manual-header {
            color: var(--text-color);
        }

        .manual-count {
            font-size: 0.8rem;
            color: var(--muted-color);
        }

        .manual-item {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            padding: 0.7rem 0.75rem;
            border-bottom: 1px solid var(--surface-border);
            transition: background 0.2s ease;
        }

        .manual-item:last-child {
            border-bottom: none;
        }

        .manual-item:hover {
            background: var(--surface-hover);
        }

        /* Extension chip. Two colours only — anything that is not a PDF is a
           Word document, since those are the only two types that upload. */
        .manual-type {
            flex-shrink: 0;
            width: 2.1rem;
            height: 2.1rem;
            border-radius: 4px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 0.8rem;
            font-weight: 700;
            letter-spacing: 0.02em;
            color: #ffffff;
        }

        .manual-type.pdf {
            background: #c0392b;
        }

        .manual-type.docx {
            background: #2b5eb0;
        }

        .manual-details {
            flex: 1;
            min-width: 0;
        }

        .manual-name {
            margin: 0;
            font-weight: 600;
            font-size: 0.875rem;
            color: var(--text-color);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .manual-meta {
            font-size: 0.75rem;
            color: var(--muted-color);
        }

        :host ::ng-deep .manual-download-btn.p-button {
            flex-shrink: 0;
            width: 2.1rem;
            height: 2.1rem;
            padding: 0;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 6px;
        }

        .manual-view-all {
            color: var(--p-primary-color);
            font-weight: 600;
        }
        /* Role chips stay inside the brand palette rather than borrowing
           Tailwind's amber/indigo, which are not PSA BDP colours. */
        .role-badge {
            color: #000000;
        }
        .role-badge-super {
            background: var(--bdp-light-gray-deep);
        }
        .role-badge-user {
            background: color-mix(in srgb, var(--bdp-blue), white 80%);
        }
        .clr-btn {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            right: 0.3rem;
        }
        .notification-loader-container {
            min-height: 12rem;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(255, 255, 255, 0.6);
            backdrop-filter: blur(2px);
        }
        .notification-loader {
            width: 50px;
            aspect-ratio: 1;
            border-radius: 50%;
            background:
                radial-gradient(farthest-side, var(--p-primary-color, #3522c4) 94%, #0000) top/8px 8px no-repeat,
                conic-gradient(#0000 30%, var(--p-primary-color, #3522c4));
            -webkit-mask: radial-gradient(farthest-side, #0000 calc(100% - 7px), #000 0);
            animation: notification-loader-spin 1s infinite linear;
        }
        @keyframes notification-loader-spin {
            100% {
                transform: rotate(1turn);
            }
        }

        /* ============================= */
        /* ANNOUNCEMENTS DIALOG          */
        /* ============================= */

        /* No :host prefix: the dialog now lives on <body>, outside this
           component's host element, so a host-scoped selector would never match
           it. The styleClass is unique to this dialog — the announcement
           management screen has its own announcement-dialog class — which is
           what keeps these global rules from reaching anything else. */
        ::ng-deep .announcement-viewer-dialog.p-dialog {
            border-radius: 12px;
            overflow: hidden;
            border: 1px solid var(--surface-border);
        }

        ::ng-deep .announcement-viewer-dialog .p-dialog-content {
            padding: 0;
            background: var(--panel-bg);
        }

        .announcement-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 1rem;
            padding: 1.15rem 1.5rem;
            border-bottom: 1px solid var(--surface-border);
        }

        .announcement-header h2 {
            margin: 0;
            font-size: 1.3rem;
            font-weight: 700;
            color: var(--heading-color);
        }

        .announcement-close-btn {
            background: transparent;
            border: none;
            color: var(--text-color);
            width: 2rem;
            height: 2rem;
            border-radius: 6px;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            transition: background 0.2s ease;
        }

         .announcement-download-btn:hover:not(:disabled) {
            background: color-mix(in srgb, var(--text-color) 10%, transparent);
        }

        .announcement-download-btn:disabled {
            opacity: 0.35;
            cursor: not-allowed;
            background: transparent;
        }

        .announcement-download-btn:disabled {
            opacity: 0.35;
            cursor: not-allowed;
            background: transparent;
        }

        .announcement-content {
            padding: 1rem 1.5rem 1.5rem;
            max-height: 60vh;
            overflow-y: auto;
        }

        .announcement-list {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
            
        }

        .announcement-card {
            border-radius: 3px;
            overflow: hidden;
            background: #f5f5f5;
            // color: #222222;
        }

        .announcement-card-header {
            display: flex;
            align-items: center;
            width: 100%;
            background: var(--p-primary-color);
            color: #fff;
        }

        .announcement-toggle {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            flex: 1;
            min-width: 0;
            padding: 0.85rem 1.15rem;
            background: transparent;
            border: none;
            text-align: left;
            cursor: pointer;
            transition: background 0.2s ease;
            color: #fff;

        }

        // .announcement-toggle:hover {
        //     background: var(--surface-hover);
        // }

        .announcement-download-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            margin-right: 0.9rem;
            padding: 0.35rem;
            background: transparent;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            // color: var(--text-color);
            transition: background 0.2s ease;
        }

        .announcement-download-btn:hover {
            background: color-mix(in srgb, var(--text-color) 10%, transparent);
        }

        .announcement-download-btn i {
            font-size: 1.15rem;
        }

        .announcement-card.expanded {
            // border-color: color-mix(in srgb, var(--primary-color) 45%, var(--surface-border));
        }

        .announcement-toggle-icon {
            font-size: 0.75rem;
            flex-shrink: 0;
        }

        .announcement-card-header h3 {
            margin: 0;
            flex: 1;
            font-size: 1rem;
            font-weight: 500;
            letter-spacing: 1px;
            text-transform: capitalize;
        }

        .announcement-date {
            font-size: 0.8rem;
            // color: var(--muted-color);
            white-space: nowrap;
        }

        .announcement-card-body {
            padding: 0 1.15rem 1rem;
            border-top: 1px solid var(--surface-border);
        }

        .announcement-description {
            margin-top: 0.75rem;
            font-size: 0.9rem;
            // color: var(--text-muted-color);
            word-break: break-word;
            font-weight: 500;
        }

        /* Same reason as above, and scoped under the dialog class since the
           paragraphs come from innerHTML and carry no scoping attribute. */
        ::ng-deep .announcement-viewer-dialog .announcement-description p {
            margin: 0 0 0.35rem;
        }

        ::ng-deep .announcement-viewer-dialog .announcement-description p:last-child {
            margin-bottom: 0;
        }

        .no-announcements,
        .announcement-loading {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
            padding: 2.5rem 1rem;
            color: var(--muted-color);
        }

        .no-announcements i,
        .announcement-loading i {
            font-size: 1.5rem;
        }

        
    `
})
export class AppTopbar implements OnInit, OnDestroy {
    layoutService = inject(LayoutService);

    isDarkTheme = computed(() => this.layoutService.isDarkTheme());

    @ViewChild('menubutton') menuButton!: ElementRef;
    @ViewChild('notificationDropdown') notificationDropdown!: ElementRef;
    @ViewChild('userManualDropdown') userManualDropdown!: ElementRef;
    loginService = inject(LoginService);

    notificationSearch = '';
    userName = '';
    userRole = '';
    briefName = '';
    showProfile = false;
    clientId = computed(() => this.clientService.selectedClientId());
    processId = computed(() => this.clientService.selectedProcessId());
    applicationId = computed(() => this.clientService.selectedApplicationId());
    customerId = computed(() => this.clientService.selectedCustomerId());
    documentId = computed(() => this.clientService.selectedDocumentId());
    documentName = computed(() => this.clientService.selectedDocumentName());
    count = 0;
    notifications: any[] = [];
    notificationLoading = false;
    sub!: Subscription;
    countSub!: Subscription;
    userId!: string;
    currentRenderedPage: string = 'Processes';
    announcementDialog = false;
    announcements: any[] = [];
    announcementLoading = false;
    announcementCount= 0;
    announcementFlag = 0;
    announcementCountSub!: Subscription;
    expandedAnnouncementIds = new Set<number>();
    // private announcementAutoOpenChecked = false;

    userManuals: any[] = [];
    userManualLoading = false;

    constructor(
        private router: Router,
        private authService: LoginService,
        private userService: UserService,
        private clientService: ClientService,
        private notificationService: NotificationService,
        private announcementsService: AnnouncementsService,
        private userManualService: UserManualService,
        public privilegeService: PrivilegeService,
        private renderer: Renderer2
    ) {
        effect(() => {
            if (this.userService.isUserInfoSet()) {
                this.setUserNameAndRole();
            }
            this.userId = this.authService.getUserId();
            this.loadNotifications();
            // this.checkAnnouncementOnLogin();
        });
    }

    hasSelectedClient = computed(() => this.clientService.selectedClientId() !== null);

    exitClient() {
        this.clientService.clearClientId();
        this.clientService.triggerShortcutReset();
        this.router.navigate(['/home']);
    }

    ngOnInit(): void {
        if (this.authService.isAuthenticated()) {
            this.setUserNameAndRole();
            if (!this.loginService.getRenderedPage()) {
                localStorage.setItem('renderedPage', this.currentRenderedPage);
            } else {
                this.currentRenderedPage = this.loginService.getRenderedPage() || this.currentRenderedPage;
            }
            this.checkAnnouncementOnLogin();
            this.countSub = this.notificationService.countChanges().subscribe((count) => {
                this.count = count;
            });
            this.sub = this.notificationService
                .startPolling(() => ({
                    user_id: this.userId
                }))
                .subscribe((res: any) => {
                    this.count = res?.notification_count;
                    this.announcementCount = res?.announcement_count;
                    this.notificationService.setCount(this.count);
                    if(res.announcement_read_flag === 0 && res.is_priority === true){
                         this.openAnnouncement()
                    }
                });
        }
    }

    toggleRenderedPage() {
        this.currentRenderedPage = this.currentRenderedPage === 'Documents' ? 'Processes' : 'Documents';
        localStorage.setItem('renderedPage', this.currentRenderedPage);
        if (this.currentRenderedPage === 'Processes') {
            this.router.navigate([`/extraction/idp-doc-grid/${this.clientId()}`], { queryParams: { type: this.documentName() } });
        } else {
            const routerLink = `/home/extraction/transaction-logger/${this.clientId()}`;
            this.router.navigateByUrl(routerLink);
        }
    }
    setUserNameAndRole() {
        let userInfo: any = localStorage.getItem('userInfo');
        if (userInfo) {
            let parsedUserInfo = JSON.parse(userInfo);
            this.userName = `${parsedUserInfo.first_name} ${parsedUserInfo.last_name}`;
            this.userRole = parsedUserInfo.is_superuser ? 'System Super User' : parsedUserInfo.role;
            this.briefName = this.getInitials(parsedUserInfo.first_name, parsedUserInfo.last_name);
        }
    }
    getInitials(firstName: string, lastName: string): string {
        const f = firstName?.trim();
        const l = lastName?.trim();

        const fInitial = f ? f.charAt(0).toUpperCase() : '';
        const lInitial = l ? l.charAt(0).toUpperCase() : '';

        return `${fInitial}${lInitial}`;
    }

    onMenuButtonClick() {
        this.layoutService.onMenuToggle();
    }


    toggleSearchBar() {
        this.layoutService.layoutState.update((value) => ({ ...value, searchBarActive: !value.searchBarActive }));
    }

    logout() {
        sessionStorage.removeItem('announcement_auto_opened');
        this.clientService.clearClientId();
        this.clientService.clearCustomerId();
        this.clientService.clearApplicationId();
        this.loginService.logout();
        this.router.navigate(['/login']);
    }

    refresh() {
        window.location.reload();
    }

    toggleTheme() {
        this.layoutService.layoutConfig.update((state) => ({
            ...state,
            darkTheme: !state.darkTheme
        }));
        // Drop any inline background so the scheme's stylesheet takes over
        this.layoutService.updateBodyBackground();
    }
    // goToProfile() {
    //     this.router.navigate(['/extraction/user-profile']);
    // }
    openProfile() {
        this.showProfile = true;
    }
    onProfileClosed() {
        this.showProfile = false;
    }

    loadNotifications() {
        const payload: any = {
            user_id: this.userId,
            page: 1,
            page_size: 1000,
            is_delete: false,
            export: false
        };

        this.notificationLoading = true;
        this.notificationService
            .list(payload)
            .pipe(finalize(() => (this.notificationLoading = false)))
            .subscribe((res: any) => (this.notifications = res.results || res));
    }

    markRead(n: any) {
        if (!n.is_read) {
            n.is_read = true;
            this.updateCount(Math.max(this.count - 1, 0));

            this.notificationLoading = true;
            this.notificationService
                .read({
                    user_id: this.userId,
                    notification_ids: [n.id]
                })
                .pipe(finalize(() => this.loadNotifications()))
                .subscribe();
        }
        if (this.notificationDropdown) {
            this.renderer.addClass(this.notificationDropdown.nativeElement, 'hidden');
        }
        // Navigate first, then close dropdown after navigation completes
        this.router.navigate([`/extraction/idp-extraction/${n.client_id}/${n.application_id}/${n.customer_id}/${n.document_type}/${n.document_id}`], { queryParams: { txn: n.document_transaction_id } });
    }

    delete(ids: number[], isAll = false) {
        this.applyDeleteLocally(ids, isAll);
        this.notificationLoading = true;
        this.notificationService
            .delete({
                user_id: this.userId,
                notification_ids: ids,
                all_notifications: isAll
            })
            .pipe(finalize(() => this.loadNotifications()))
            .subscribe();
    }

    deleteAll() {
        this.delete([], true);
    }

    ngOnDestroy() {
        this.sub?.unsubscribe();
        this.countSub?.unsubscribe();
        this.announcementCountSub?.unsubscribe();
    }

    markAllRead() {
        const unreadCount = this.notifications.filter((notification) => !notification.is_read).length;
        if (unreadCount > 0) {
            this.notifications = this.notifications.map((notification) => ({
                ...notification,
                is_read: true
            }));
            this.updateCount(Math.max(this.count - unreadCount, 0));
        }
        this.notificationLoading = true;
        this.notificationService
            .read({
                user_id: this.userId,
                notification_ids: [],
                all_notifications: true
            })
            .pipe(finalize(() => this.loadNotifications()))
            .subscribe();
    }

    private applyDeleteLocally(ids: number[], isAll: boolean) {
        if (isAll) {
            this.notifications = [];
            this.updateCount(0);
            return;
        }

        const deletedIds = new Set(ids);
        const unreadDeletedCount = this.notifications.filter((notification) => deletedIds.has(notification.id) && !notification.is_read).length;

        this.notifications = this.notifications.filter((notification) => !deletedIds.has(notification.id));
        this.updateCount(Math.max(this.count - unreadDeletedCount, 0));
    }

    private updateCount(count: number) {
        this.count = count;
        this.notificationService.setCount(count);
    }
    openAnnouncement() {
        if (this.announcementDialog || this.announcementLoading) {
            return;
        }
        this.loadAnnouncements();
    }

    private checkAnnouncementOnLogin(): void {
        const userInfo = localStorage.getItem('userInfo');
        if (!userInfo) {
            return;
        }

        const announcementAutoOpenKey = 'announcement_auto_opened';

        if (sessionStorage.getItem(announcementAutoOpenKey) === 'true') {
            return;
        }

        try {
            const parsedUserInfo = JSON.parse(userInfo);

            this.announcementFlag = Number(
                parsedUserInfo?.announcement_read_flag ?? 0
            );

            if (this.announcementFlag === 0) {
                sessionStorage.setItem(announcementAutoOpenKey, 'true');
                this.openAnnouncement();
            }
        } catch (error) {
            console.error('Failed to check announcement flag', error);
        }
    }

    // The announcements count as read once the dialog has been opened, so re-pull
    // the count on close to pick up what the backend has cleared.
    closeAnnouncement() {
        this.announcementDialog = false;
        this.announcementCount = 0;
        this.getUserShortInfo();
    }
     getUserShortInfo() {

        this.userService.getUserShortInfo().subscribe({
            next: (res:any) => {
                localStorage.setItem('userInfo', JSON.stringify(res));
            }
        })
    }

    private loadAnnouncements() {
        if (this.announcementLoading) {
            return;
        }
        this.announcementLoading = true;
        const payload = {
            client_id: this.loginService.getClientId() ?? null,
            application_id: this.loginService.getApplicationId() ?? null,
            process_type_id: this.loginService.getProcessId() ?? null,
            customer_id: this.loginService.getCustomerId() ?? null
        };
        this.announcementsService
            .getAnnouncements(payload)
            .pipe(finalize(() => (this.announcementLoading = false)))
            .subscribe({
                next: (res: any) => {
                    // ;
                    this.announcements = res?.results ?? res?.data ?? (Array.isArray(res) ? res : []);
                    this.expandedAnnouncementIds = new Set<number>(this.announcements.length ? [this.announcements[0].id] : []);
                    this.announcementDialog = true;
                },
                error: () => {
                    this.announcements = [];
                    this.expandedAnnouncementIds.clear();
                }
            });
    }
    toggleAnnouncement(id: number): void {
        if (this.expandedAnnouncementIds.has(id)) {
            this.expandedAnnouncementIds.delete(id);
        } else {
            this.expandedAnnouncementIds.add(id);
        }
    }

    isAnnouncementExpanded(id: number): boolean {
        return this.expandedAnnouncementIds.has(id);
    }

    // The attachment comes back from the API in `file_content` - base64, with
    // or without a data URI header, or a stored path/URL.
    downloadAnnouncementFile(announcement: any): void {
        const content: string = (announcement?.file_content ?? '').trim();

        if (!content) {
            return;
        }

        const fileName = this.getAnnouncementFileName(announcement, content);

        // A path or URL can simply be linked.
        if (/^https?:\/\//i.test(content) || (!content.startsWith('data:') && /\.(pdf|docx)(\?|#|$)/i.test(content))) {
            this.triggerDownload(content, fileName);
            return;
        }

        const base64 = content.startsWith('data:') && content.includes(',') ? content.slice(content.indexOf(',') + 1) : content;

        try {
            const binary = atob(base64);
            const bytes = new Uint8Array(binary.length);

            for (let i = 0; i < binary.length; i++) {
                bytes[i] = binary.codePointAt(i) ?? 0;
            }

            const blob = new Blob([bytes], { type: this.getAnnouncementMimeType(content, fileName) });
            const url = window.URL.createObjectURL(blob);

            this.triggerDownload(url, fileName);

            window.URL.revokeObjectURL(url);
        } catch (error) {
        }
    }

    // Name from `file_content` itself: a data URI `name=` parameter, or the last
    // segment of a stored path. Plain base64 carries none, so fall back.
    private getAnnouncementFileName(announcement: any, content: string): string {
        if (content.startsWith('data:')) {
            const header = content.includes(',') ? content.slice(0, content.indexOf(',')) : content;
            const name = /(?:^|;)\s*(?:file)?name=([^;]+)/i.exec(header)?.[1];

            if (name) {
                return this.decodeFileName(name.replace(/^"|"$/g, ''));
            }
        } else if (/\.(pdf|docx)(\?|#|$)/i.test(content)) {
            const lastSegment = content.split(/[?#]/)[0].split(/[\\/]/).pop();

            if (lastSegment) {
                return this.decodeFileName(lastSegment);
            }
        }

        const extension = this.isDocx(content) ? 'docx' : 'pdf';

        return `${announcement?.title || 'attachment'}.${extension}`;
    }

    private getAnnouncementMimeType(content: string, fileName: string): string {
        return /\.docx$/i.test(fileName) || this.isDocx(content) ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' : 'application/pdf';
    }

    // "%PDF-" encodes to JVBERi, and the "PK" zip header a DOCX is packaged in
    // encodes to UEsDB.
    private isDocx(content: string): boolean {
        const base64 = content.startsWith('data:') && content.includes(',') ? content.slice(content.indexOf(',') + 1) : content;

        return /^data:[^;,]*wordprocessingml/i.test(content) || base64.startsWith('UEsDB');
    }

    private decodeFileName(value: string): string {
        try {
            return decodeURIComponent(value.trim());
        } catch {
            return value.trim();
        }
    }

    private triggerDownload(url: string, fileName: string): void {
        const link = document.createElement('a');

        link.href = url;
        link.download = fileName;
        link.click();
    }
    // =========================================================================
    // USER MANUALS
    // =========================================================================

    // Scoped the same way announcements are — the API narrows the list to what
    // the user's current client / application / process / customer covers.
    loadUserManuals(): void {
        const payload = {
            client_id: this.loginService.getClientId() ?? null,
            application_id: this.loginService.getApplicationId() ?? null,
            process_type_id: this.loginService.getProcessId() ?? null,
            customer_id: this.loginService.getCustomerId() ?? null,
            page: 1,
            page_size: 100,
            export: false
        };

        this.userManualLoading = true;

        this.userManualService
            .getUserManuals(payload)
            .pipe(finalize(() => (this.userManualLoading = false)))
            .subscribe({
                next: (res: any) => {
                    this.userManuals = res?.results ?? res?.data ?? (Array.isArray(res) ? res : []);
                },
                error: () => {
                    this.userManuals = [];
                }
            });
    }

    /** Display name, falling back to the stored file name. */
    manualFileName(manual: any): string {
        return manual?.name || this.storedFileName(manual) || 'User manual';
    }

    /** Drives the coloured chip: PDF or, for everything else, Word. */
    manualExtension(manual: any): 'pdf' | 'docx' {
        const candidate = `${this.storedFileName(manual) ?? ''} ${manual?.file_type ?? ''}`.toLowerCase();

        return candidate.includes('docx') || candidate.includes('wordprocessingml') || candidate.includes('msword') ? 'docx' : 'pdf';
    }

    /** "v1.3 · 2.4 MB" — either half is dropped when the API does not send it. */
    manualMeta(manual: any): string {
        const version = manual?.version ?? manual?.file_version ?? null;
        const size = this.formatFileSize(manual?.file_size ?? manual?.size ?? null);

        return [version ? `v${String(version).replace(/^v/i, '')}` : null, size].filter(Boolean).join(' · ');
    }

    private storedFileName(manual: any): string | null {
        const candidate = manual?.file_name || manual?.original_file_name || manual?.file || null;

        if (typeof candidate !== 'string' || !candidate.trim()) {
            return null;
        }

        return candidate.trim().split(/[\\/]/).pop() || null;
    }

    private formatFileSize(bytes: any): string | null {
        const size = Number(bytes);

        if (!size || Number.isNaN(size)) {
            return null;
        }

        if (size < 1024 * 1024) {
            return `${Math.round(size / 1024)} KB`;
        }

        return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    }

    downloadUserManual(manual: any): void {
        if (!manual?.id) {
            return;
        }

        this.userManualService.downloadUserManual(manual.id).subscribe({
            next: (response: Blob) => {
                const fileName = this.storedFileName(manual) ?? `${this.manualFileName(manual)}.${this.manualExtension(manual)}`;

                const url = window.URL.createObjectURL(response);

                this.triggerDownload(url, fileName);

                window.URL.revokeObjectURL(url);
            },
            error: () => {}
        });
    }

    goToUserManuals(): void {
        if (this.userManualDropdown) {
            this.renderer.addClass(this.userManualDropdown.nativeElement, 'hidden');
        }

        this.router.navigate([`/home/administration/user-manual/${this.loginService.getClientId() ?? 0}`]);
    }

}
